import { Component, computed, Directive, inject, Inject, OnDestroy, signal, ViewChild } from '@angular/core';
import { FeedstockService } from '../../../../services/feedstockservice';
import { GeneratorInput, SaveStreamRequest } from '../../../../interfaces/generatorsmodel';
import { IdName } from '../../../../interfaces/pricematrixmodel';
import { LookUpService } from '../../../../services/lookupservice';
import { GetDisposalMethodsResponse } from '../../../../interfaces/feedstockstatus';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { forkJoin, Subject, takeUntil } from 'rxjs';
import { Regions } from '../../../../interfaces/regions';
import { AbstractControl, FormsModule, NG_VALIDATORS, NgForm, ValidationErrors, Validator } from '@angular/forms'; 
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleChange, MatSlideToggleModule } from '@angular/material/slide-toggle';
import { StreamInput, StreamsList } from '../../../../interfaces/streamsmodel';
import { MatIconModule } from '@angular/material/icon';
import { MatExpansionModule } from '@angular/material/expansion';
import { SiteService } from '../../../../services/siteservice';
import { Cities } from '../../../../interfaces/cities';
import { States } from '../../../../interfaces/states';
import { MatDialogModule } from '@angular/material/dialog';
import { numericInputLimit } from '../../../shared/commonutils/helper';
import { RolePermissionsService } from '../../../../services/role-permissions.service';
import { LoadScheduleControlFlags, ScreenName } from '../../../../interfaces/securityPermissions';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { RoleService } from '../../../../services/role.service';

@Directive({
  selector: '[appMultiEmail]',
  providers: [{ provide: NG_VALIDATORS, useExisting: MultiEmailValidatorDirective, multi: true }]
})
export class MultiEmailValidatorDirective implements Validator {
  private emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  validate(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (!value || value.toString().trim() === '') {
      return null;
    }
    const rawTokens = value.split(/[;,]+/);
    const cleanEmails = rawTokens.map((e: string) => e.trim()).filter((e: string) => e.length > 0);
    const hasEmptySegments = rawTokens.filter((t: { trim: () => { (): any; new(): any; length: number; }; }) => t.trim().length > 0).length !== rawTokens.length;
    const endsWithSeparator = /[;,]\s*$/.test(value);

    if (endsWithSeparator || cleanEmails.length === 0) {
       return { multiEmail: true };
    }
    const allValid = cleanEmails.every((email: string) => this.emailRegex.test(email));

    return allValid ? null : { multiEmail: true };
  }
}

@Component({
  selector: 'app-addedit-stream',
  imports: [
    CommonModule,
    FormsModule, // FIX: ngModel error
    MatCardModule,
    MatFormFieldModule, // FIX: mat-form-field error
    MatInputModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatButtonModule,
    MatIconModule,
    MatExpansionModule,
    MatDialogModule,
    MultiEmailValidatorDirective,
    DragDropModule
  ],
  templateUrl: './addedit-stream.component.html',
  styleUrl: './addedit-stream.component.scss'
})
export class AddeditStreamComponent implements OnDestroy {
  permissions = inject(RolePermissionsService);
  initialModel: SaveStreamRequest ={
    id: 0,
    stream: '',
    minimalCharges: 0,
    codRequired: false,
    codEmail: '',
    status: true,
    isTote: false,
    commissionEligible: false,
    rebateEligible: false,
    streamFood: null,
    streamMatrix: null,
    customerID: 0,
    salesRep: null,
    salesRepId: null,
    siteNumber: null,
    noOfBins: null,
    haulerId: null,
    strengthID: null,
    projectedVolume: 0,
    disposalMethodId: null,
    opportunityID: '',
    marketID: 0,
    pickupLocationName: null,
    addressLine1: null,
    addressLine2: null,
    city: null,
    state: null,
    zipCode: null,
    municipality: null,
    sameAsCustomerAddress: false,
    stateId: null,
    bdr: null,
    isExpense: false,
    isOutBound: false,
    selfHaulEmail: null,
    selfHaulPhone: null,
    isCustomerReferenceRequired: false
  }
  @ViewChild('streamForm') streamForm!: NgForm;
  streamData = signal<SaveStreamRequest>(this.initialModel);
  isSaving = signal(false);
  detailsExpanded = signal(true);
  pickupExpanded = signal(false);
  saveSuccess = false;
  saveError = '';
  task: string = "Add";
  
  foodGroups = signal<IdName[]>([]);
  filteredFoodGroups = signal<IdName[]>([]);
  foodMatrices = signal<IdName[]>([]);
  streamStrengths = signal<IdName[]>([]);
  filteredStreamStrengths = signal<IdName[]>([]);
  salesReps = signal<IdName[]>([]);
  filteredSalesReps = signal<IdName[]>([]);
  haulers = signal<IdName[]>([]);
  filteredHaulers = signal<IdName[]>([]);
  disposalMethods = signal<GetDisposalMethodsResponse[]>([]);
  filteredDisposalMethods = signal<GetDisposalMethodsResponse[]>([]);
  streamRegions = signal<Regions[]>([]);
  filteredStreamRegions = signal<Regions[]>([]);
  displayValue = signal<string>('');
  genId: string = "";
  title: string = "";

  pickupLocationName = signal<string>('');
  sameAsCustomerAddress = signal<boolean>(false);
  
  addressLine1 = signal<string | null>(null);
  addressLine2 = signal<string | null>(null);
  city = signal<string | null>(null);
  state = signal<string | null>(null);
  zipCode = signal<string | null>(null);
  municipality = signal<string | null>(null);
  bdrid = signal<number | null>(null);
  
  marketID = signal<number | null>(-1);
  marketOptions = signal<Regions[]>([]);
  states = signal<States[]>([]);
  filteredStates = signal<States[]>([]);
  cities = signal<Cities[]>([]);
  filteredCities = signal<Cities[]>([]);
  bdrEmployees = signal<IdName[]>([]);
  filteredbdrEmployees = signal<IdName[]>([]);

  private destroy$ = new Subject<void>();
  enableControl = computed(() => true); // Placeholder logic
  projectedVolumeAndHaulerEnabled = computed(() => true); // Placeholder logic
  controlPermissions: LoadScheduleControlFlags = new LoadScheduleControlFlags();
  canSave: boolean = false;
  constructor(public dialogRef: MatDialogRef<AddeditStreamComponent>,
    private fsService: FeedstockService, private lookup: LookUpService,
    @Inject(MAT_DIALOG_DATA) public data: StreamInput,
    private siteService: SiteService, private rolesService: RoleService) {
    this.genId = `${data.data.generatorId}`;
    this.task = data.data.task;
    this.loadDropdownData();
    if(this.task === 'Edit'){
      this.title = "(" + data.data.streamName + ")";
    }
    this.canSave = data.canSave;
    // if(this.actionStatus().ShowStreamProjectedVolumeAndHauler){
    //   this.projectedVolumeAndHaulerEnabled = computed(() => true);
    //   this.enableControl = computed(() => false);
    // }else{
    //   this.projectedVolumeAndHaulerEnabled = computed(() => true);
    //   this.enableControl = computed(() => true);
    // }
    console.log("incoming stream data: ", data);
  }

  onCODChange(value: boolean): void {
    this.streamData.update(data => ({
      ...data,
      codRequired: value,
      codEmail: value ? data.codEmail : '',
    }));
  }
  
  onStateChanged(selectedState: number, isOnLoad?: boolean): void {
    if (selectedState) {
      this.siteService.getCitiesByState(selectedState).subscribe({
        next: (response: Cities[]) => {
          this.cities.set(response);
          this.filteredCities.set(response);
          if (isOnLoad && this.data.data.city) {
            const selectedCity = this.cities().find(
              (c) => c.city?.toLowerCase() === this.data?.data?.city?.toLowerCase()
            );
            if (selectedCity) {
              this.updateCityAndStateSelection(selectedCity.city, selectedState);
            }
          }
        },
      });
    }
    else{
      this.cities.set([]);
      this.filteredCities.set([]);
      this.streamData.update(data => ({
        ...data,
        stateId: null,
        city: null
      }));
      this.updateCityAndStateSelection('', null);
    }
  }  
  
  updateCityAndStateSelection(city: string, state: any): void {
    this.city.set(city);
    this.state.set(state);
    this.streamData.update(data => ({
      ...data,
      city: city,
      //stateId: state?.id ?? null
    }));
  }

  loadDropdownData() {
    let roles = this.rolesService.getRolesAsString();
    const foodGroups$ = this.lookup.getFoodGroup();
    const foodMatrices$ = this.lookup.getFoodMatrix();
    const streamStrengths$ = this.lookup.getStreamStrengthLookUp();
    const salesReps$ = this.lookup.getSalesRep();
    const haulers$ = this.lookup.getORHaulers();
    const disposalMethods$ = this.lookup.getAllDisposalMethods();
    const streamRegions$ = this.lookup.getMarketsList("");
    const statesData$ = this.lookup.getStatesList();
    const bdrEmployees$ = this.lookup.getAllEmployeesLookUp();
    const perms$ = this.permissions.getRolePermissionsNew(roles, ScreenName.StreamPopup, this.task === 'Edit' ? this.data.data.streamId : 0)

    forkJoin([
      perms$,
      foodGroups$,
      foodMatrices$,
      streamStrengths$,
      salesReps$,
      haulers$,
      disposalMethods$,
      streamRegions$,
      statesData$,
      bdrEmployees$
    ])
    .pipe(
      takeUntil(this.destroy$)
    )
    .subscribe({
      next: ([
        perms,
        foodGroups,
        foodMatrices,
        streamStrengths,
        salesReps,
        haulers,
        disposalMethods,
        streamRegions,
        statesData,
        bdrEmployees
      ]) => {
        // 4. Update the signals using the .set() method with the actual data
        console.log("perms", perms);
        this.controlPermissions = perms;
        this.foodGroups.set(foodGroups);
        this.filteredFoodGroups.set(foodGroups);
        this.foodMatrices.set(foodMatrices);
        this.streamStrengths.set(streamStrengths);
        this.filteredStreamStrengths.set(streamStrengths);
        this.salesReps.set(salesReps);
        this.filteredSalesReps.set(salesReps);
        this.haulers.set(haulers);
        this.filteredHaulers.set(haulers);
        this.disposalMethods.set(disposalMethods);
        this.filteredDisposalMethods.set(disposalMethods);
        this.streamRegions.set(streamRegions);
        this.filteredStreamRegions.set(streamRegions);

        this.bdrEmployees.set(bdrEmployees);
        this.filteredbdrEmployees.set(bdrEmployees);

        this.states.set(statesData);
        this.filteredStates.set(statesData);

        console.log('Streams Dropdown data loaded successfully.');

        this.initializeFormFromData(this.data.data);
      }
    });
  }
  
  initializeFormFromData(streamsListData: StreamsList): void {
    if (streamsListData && streamsListData.streamId) {
      console.log("edit data", streamsListData);
      const salesRepName = streamsListData.salesRep;
      let selectedStateId: number | null = null;
      let stateAbbreviation = streamsListData.state;
      let salesRepId: number | null = null;
      
      if (salesRepName) {
          const foundRep = this.salesReps().find(rep => rep.name === salesRepName);
          salesRepId = foundRep ? foundRep.id : null; 
      }
      if (stateAbbreviation) {
        const selectedState = this.states().find((s) => s.state === stateAbbreviation);
        if (selectedState?.id) {
          selectedStateId = selectedState.id; 
        }
      }
      const initialStreamData: SaveStreamRequest = {
        id: streamsListData.streamId || 0,
        stream: streamsListData.streamName || '',
        streamFood: streamsListData.foodGroupId,
        streamMatrix: streamsListData.foodMatrixId,
        strengthID: streamsListData.strengthID,
        projectedVolume: streamsListData.projectedVolume || 0,
        siteNumber: streamsListData.siteNumber,
        noOfBins: streamsListData.noOfBins ?? null,
        salesRep: '', //`${salesRepId}`,
        salesRepId: null, //salesRepId,
        haulerId: streamsListData.haulerId || null,
        disposalMethodId: streamsListData.disposalMethodId,
        status: streamsListData.status || false,
        isTote: streamsListData.tote || false,
        commissionEligible: streamsListData.commissionEligible || false,
        rebateEligible: streamsListData.rebateEligible || false,
        codRequired: streamsListData.codRequired || false,
        codEmail: streamsListData.codEmail || '',
        minimalCharges: 0,
        customerID: parseInt(this.genId),
        opportunityID: streamsListData.opportunityId || '',
        marketID: streamsListData.market,

        pickupLocationName: streamsListData.pickupLocationName || '',
        addressLine1: streamsListData.addressLine1 || '',
        addressLine2: streamsListData.addressLine2 || '',
        city: null,
        state: null,
        stateId: null ,
        zipCode: streamsListData.zipCode || '',
        municipality: streamsListData.municipality || '',
        sameAsCustomerAddress: streamsListData.sameAsCustomerAddress || false,
        bdr: streamsListData.bdr ?? null,
        isExpense: streamsListData.isExpense || false,
        isOutBound: streamsListData.isOutBound || false,
        selfHaulEmail: streamsListData.selfHaulEmail || '',
        selfHaulPhone: streamsListData.selfHaulPhone || '',
        isCustomerReferenceRequired: streamsListData.isCustomerReferenceRequired || false
      };

      this.sameAsCustomerAddress.set(streamsListData.sameAsCustomerAddress || false);
      
      // Set the signal state
      this.streamData.set(initialStreamData);
      
      if (streamsListData.state) {
        const selectedState = this.states().find(
          (s) => s.state === streamsListData.state
        );
        if (selectedState?.id) {
          this.streamData.update(d => ({ ...d, stateId: selectedState.id }));
          //this.onStateChanged(selectedState.id, true);
          this.siteService.getCitiesByState(selectedState.id).subscribe({
            next: (cities: Cities[]) => {
              this.cities.set(cities);
              this.filteredCities.set(cities);
              if (streamsListData.city) {
                const match = cities.find(
                  c => c.city?.toLowerCase() === streamsListData.city?.toLowerCase()
                );
                if (match) {
                  this.streamData.update(d => ({ ...d, city: match.city }));
                }
              }
            }
          });
        }
      }
    }
  }
  
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  roundOff(event: Event, maxValue: number, maxDecimals: number, ctrlName: string): void {
    const input = event.target as HTMLInputElement;
    let val = input.value;
    
    val = numericInputLimit(event, maxValue, maxDecimals);
    
    this.displayValue.set(val);
    input.value = val;

    // 6. Parsing for the backing variables
    const isValueEmpty = val === '' || val === '.';
    const parsedValue = maxDecimals === 0 ? parseInt(val, 10) : parseFloat(val);
    const finalValue = isValueEmpty ? null : parsedValue;
    
    this.streamData.update(data => ({
      ...data,
      [ctrlName]: finalValue
    }));
    requestAnimationFrame(() => {
      if (document.activeElement === input) {
          input.value = val;
      }
    });
  }

  saveStreamData() {
    this.streamForm.form.markAllAsTouched();
    this.streamForm.form.updateValueAndValidity();
    const controls = this.streamForm.form.controls;

    const detailFields = ['stream', 'haulerId'];
    const pickupFields = ['locationName', 'addressLine1', 'state', 'city', 'zipCode'];

    const hasDetailErrors = detailFields.some(field => controls[field]?.invalid);
    const hasPickupErrors = pickupFields.some(field => controls[field]?.invalid);

    if (this.streamForm.invalid) {
      this.saveError = 'Please fill in all mandatory fields.';
      this.saveSuccess = false;
      if (hasDetailErrors) {
        this.detailsExpanded.set(true);
        this.pickupExpanded.set(false);
      } else if (hasPickupErrors) {
        this.pickupExpanded.set(true);
        this.detailsExpanded.set(false);
      }
      return;
    }
    const data = this.streamData();
    if (!data.stream) {
      this.saveError = 'Stream is mandatory.';
      this.saveSuccess = false;
      return;
    }
    if (!data.haulerId) {
      this.saveError = 'Hauler is mandatory.';
      this.saveSuccess = false;
      return;
    }
    
    if (!data.sameAsCustomerAddress && (!data.city || data.city.trim() === '')) {
      this.saveError = 'City is mandatory.';
      this.pickupExpanded.set(true);
      return;
    }

    this.isSaving.set(true);
    this.saveError = '';
    this.saveSuccess = false;
    const salesRepId = data.salesRepId;
    let salesRepName = '';

    if (salesRepId !== null && salesRepId !== undefined) {
        const selectedRep = this.salesReps().find(rep => rep.id === salesRepId);
        salesRepName = selectedRep ? selectedRep.name : '';
    }

    let stateName = '';
    if (data.stateId !== null && data.stateId !== undefined) {
      const selectedState = this.states().find(state => state.id === data.stateId);
      stateName = selectedState ? selectedState.state : '';
    }

    var payload: SaveStreamRequest = {
      id: data.id,
      stream: data.stream,
      streamFood: data.streamFood,
      streamMatrix: data.streamMatrix,
      strengthID: data.strengthID,
      projectedVolume: data.projectedVolume,
      siteNumber: data.siteNumber,
      noOfBins: data.noOfBins,
      salesRep: salesRepName,
      haulerId: data.haulerId,
      disposalMethodId: data.disposalMethodId,
      marketID: data.marketID,
      opportunityID: data.opportunityID,
      status: data.status,
      isTote: data.isTote,
      commissionEligible: data.commissionEligible,
      rebateEligible: data.rebateEligible,
      codRequired: data.codRequired,
      codEmail: data.codEmail,
      minimalCharges: 0,
      customerID: parseInt(this.genId),
      salesRepId: data.salesRepId,
      
      pickupLocationName: data.pickupLocationName,
      addressLine1: data.addressLine1,
      addressLine2: data.addressLine2,
      city: data.city,
      state: stateName,
      stateId: data.state ? parseInt(data.state) : null,
      zipCode: data.zipCode,
      municipality: data.municipality,
      sameAsCustomerAddress: data.sameAsCustomerAddress,
      bdr: data.bdr ?? null,
      isExpense: data.isExpense,
      isOutBound: data.isOutBound,
      selfHaulEmail: data.selfHaulEmail,
      selfHaulPhone: data.selfHaulPhone,
      isCustomerReferenceRequired: data.isCustomerReferenceRequired
    };

    console.log('stream data to save:', payload);

    this.fsService.saveStream(payload).subscribe({
      next: (response) => {
        console.log('Stream Save successful:', response);
        this.dialogRef.close(true);
      }
    });
  }
  
  onInputChange(key: keyof SaveStreamRequest, value: any): void {
    this.streamData.update(data => ({
      ...data,
      [key]: value
    }));
  }

  
  phoneKeydown(event: KeyboardEvent): void {
    const allowedKeys = [
      'Backspace',
      'Delete',
      'Tab',
      'ArrowLeft',
      'ArrowRight',
      'Home',
      'End'
    ];
  
    if (allowedKeys.includes(event.key) || event.ctrlKey || event.metaKey) {
      return;
    }
  
    if (!/^\d$/.test(event.key)) {
      event.preventDefault();
    }
  }

  formatPhoneNumber(event: Event, key: string): void {
    const input = event.target as HTMLInputElement;
    let value = input.value.replace(/\D/g, '').substring(0, 10);
    let formattedValue = '';
    if (value.length > 0) {
      formattedValue = '(' + value.substring(0, 3);
    }
    if (value.length >= 4) {
      formattedValue += ') ' + value.substring(3, 6);
    }
    if (value.length >= 7) {
      formattedValue += '-' + value.substring(6, 10);
    }
    input.value = formattedValue;
    
    this.streamData.update(data => ({
      ...data,
      [key]: formattedValue
    }));
    //input.value = formattedValue;
    //this.cdr.detectChanges();
  }

  onAddressCheck(event: MatSlideToggleChange): void {
    this.sameAsCustomerAddress.set(event.checked);
    if (event.checked) {
      this.clearAddressSignals();
    }
  }
  

  clearAddressSignals(): void {
    this.addressLine1.set(null);
    this.addressLine2.set(null);
    this.city.set(null);
    this.state.set(null);
    this.zipCode.set(null);
    this.municipality.set(null);
  }

  closeModal(){
    this.dialogRef.close(false);
  }

  filterMarkets(event: Event): void {
    const searchTerm = (event.target as HTMLInputElement).value.toLowerCase();
    const fullList = this.streamRegions();
    if (!searchTerm) {
      this.filteredStreamRegions.set(fullList);
    }
      else {      const filtered = fullList.filter(
        g => g.regionName?.toLowerCase().includes(searchTerm)
      );
      this.filteredStreamRegions.set(filtered);
    }
  }

  filterSalesReps(event: Event): void {
    const searchTerm = (event.target as HTMLInputElement).value.toLowerCase();
    const fullList = this.salesReps();     
    if (!searchTerm) {
      this.filteredSalesReps.set(fullList); 
    } else {
      const filtered = fullList.filter(
        g => g.name.toLowerCase().includes(searchTerm)
      );
      this.filteredSalesReps.set(filtered); 
    }
  }

  filterBdrEmployees(event: Event): void {
    const searchTerm = (event.target as HTMLInputElement).value.toLowerCase();
    const fullList = this.bdrEmployees();
    if (!searchTerm) {
      this.filteredbdrEmployees.set(fullList);
    } else {
      const filtered = fullList.filter(
        g => g.name.toLowerCase().includes(searchTerm)
      );
      this.filteredbdrEmployees.set(filtered);
    }
  }
  

  filterStreamGroups(event: Event): void {
    const searchTerm = (event.target as HTMLInputElement).value.toLowerCase();
    const fullList = this.foodGroups();
    if (!searchTerm) {
      this.filteredFoodGroups.set(fullList);
    } else {
      const filtered = fullList.filter(
        g => g.name.toLowerCase().includes(searchTerm)
      );
      this.filteredFoodGroups.set(filtered);
    }
  }

  filterStreamStrength(event: Event): void {
    const searchTerm = (event.target as HTMLInputElement).value.toLowerCase();
    const fullList = this.streamStrengths();
    if (!searchTerm) {
      this.filteredStreamStrengths.set(fullList);
    } else {
      const filtered = fullList.filter(
        g => g.name.toLowerCase().includes(searchTerm)
      );
      this.filteredStreamStrengths.set(filtered);
    }
  }

  filterHaulers(event: Event): void {
    const searchTerm = (event.target as HTMLInputElement).value.toLowerCase();
    const fullList = this.haulers();     
    if (!searchTerm) {
      this.filteredHaulers.set(fullList); 
    } else {
      const filtered = fullList.filter(
        g => g.name.toLowerCase().includes(searchTerm)
      );
      this.filteredHaulers.set(filtered); 
    }
  }

  filterDisposalMethods(event: Event): void {
    const searchTerm = (event.target as HTMLInputElement).value.toLowerCase();
    const fullList = this.disposalMethods();     
    if (!searchTerm) {
      this.filteredDisposalMethods.set(fullList); 
    } else {
      const filtered = fullList.filter(
        g => g.disposalMethod?.toLowerCase().includes(searchTerm)
      );
      this.filteredDisposalMethods.set(filtered); 
    }
  }

  filterStreamRegions(event: Event): void {
    const searchTerm = (event.target as HTMLInputElement).value.toLowerCase();
    const fullList = this.streamRegions();     
    if (!searchTerm) {
      this.filteredStreamRegions.set(fullList); 
    } else {
      const filtered = fullList.filter(
        g => g.regionName?.toLowerCase().includes(searchTerm)
      );
      this.filteredStreamRegions.set(filtered); 
    }
  }

  filterState(event: Event): void {
    const searchTerm = (event.target as HTMLInputElement).value.toLowerCase();
    if (!searchTerm) {
      this.filteredStates.set([...this.states()]);
    } else {
      this.filteredStates.set(this.states().filter(
        state => state.state.toLowerCase().includes(searchTerm)
      ));
    }
  }
    
  filterCity(event: Event): void {
    const searchTerm = (event.target as HTMLInputElement).value.toLowerCase();
    if (!searchTerm) {
      this.filteredCities.set([...this.cities()]);
    } else {
      this.filteredCities.set(this.cities().filter(
        city => city.city.toLowerCase().includes(searchTerm)
      ));
    }
  }
}
